import React, { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, ReceiptText, Store, X } from 'lucide-react';
import type { ShoppingListItem } from '../../../shared/types';
interface CompleteTripDialogProps {
  isOpen: boolean;
  items: ShoppingListItem[];
  onClose: () => void;
  onConfirm: (actualTotals: Record<string, number>) => void;
}
export function CompleteTripDialog({
  isOpen,
  items,
  onClose,
  onConfirm
}: CompleteTripDialogProps) {
  const groups = useMemo(() => groupItemsByStore(items), [items]);
  const estimatedTotals = useMemo(() => Object.fromEntries(Object.entries(groups).map(([store, storeItems]) => [store, storeItems.reduce((total, item) => total + item.estimatedPrice * item.quantity, 0)])), [groups]);
  const [actualTotals, setActualTotals] = useState<Record<string, string>>({});
  useEffect(() => {
    if (isOpen) {
      setActualTotals(Object.fromEntries(Object.entries(estimatedTotals).map(([store, total]) => [store, total.toFixed(2)])));
    }
  }, [estimatedTotals, isOpen]);
  const estimatedGrandTotal = Object.values(estimatedTotals).reduce((total, amount) => total + amount, 0);
  const actualGrandTotal = Object.values(actualTotals).reduce((total, amount) => total + (Number.parseFloat(amount.replace(',', '.')) || 0), 0);
  const hasInvalidTotal = Object.keys(groups).some(store => {
    const value = Number.parseFloat((actualTotals[store] ?? '').replace(',', '.'));
    return Number.isNaN(value) || value < 0;
  });
  const totalDifference = actualGrandTotal - estimatedGrandTotal;
  const hasMeaningfulDifference = !hasInvalidTotal && Math.abs(totalDifference) >= 0.01;
  const handleConfirm = () => {
    if (hasInvalidTotal) return;
    onConfirm(Object.fromEntries(Object.keys(groups).map(store => [store, Number.parseFloat(actualTotals[store].replace(',', '.'))])));
  };
  return <AnimatePresence data-id="element-2075" data-mp="qa3uv6w68y7p">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 z-50 flex items-end bg-black/60 backdrop-blur-sm sm:items-center sm:justify-center sm:p-4" onClick={onClose} data-id="element-2076" data-mp="v96x6jkroqbz">
          <motion.section role="dialog" aria-modal="true" aria-labelledby="complete-trip-title" initial={{
        opacity: 0,
        y: '100%'
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 280,
        damping: 28
      }} onClick={event => event.stopPropagation()} className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-card shadow-2xl sm:rounded-3xl" data-id="element-2077" data-mp="190brd65d8qex">
            <header className="flex items-start justify-between gap-4 border-b border-border px-6 py-5" data-id="element-2078" data-mp="21vryj7dtbtf6">
              <div className="flex items-center gap-3" data-id="element-2079" data-mp="1x38epsoj821x">
                <div className="rounded-xl bg-primary/10 p-2.5 text-primary" data-id="element-2080" data-mp="1t26lptl7km8e">
                  <ReceiptText className="h-5 w-5" data-id="element-2081" data-mp="1t027oitli6ue" />
                </div>
                <div data-id="element-2082" data-mp="1o4wmjm80ow7d">
                  <h2 id="complete-trip-title" className="text-lg font-semibold text-foreground" data-id="element-2083" data-mp="1o1ikkf39mp0g">
                    Wat heb je betaald?
                  </h2>
                  <p className="mt-0.5 text-sm text-muted-foreground" data-id="element-2084" data-mp="2gtc0e5fex8h4">
                    Vul per winkel je kassabon in. Zo blijven je uitgaven en voorraad kloppen.
                  </p>
                </div>
              </div>
              <button type="button" onClick={onClose} className="rounded-xl p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary" aria-label="Sluiten" data-id="element-2085" data-mp="26vxkc6xeilpc">
                <X className="h-5 w-5" data-id="element-2086" data-mp="2j9j6mjg5zhau" />
              </button>
            </header>

            <div className="flex-1 space-y-4 overflow-y-auto px-6 py-5" data-id="element-2087" data-mp="2o46y8r6gsrtn">
              {Object.entries(groups).map(([store, storeItems]) => {
            const estimatedTotal = estimatedTotals[store];
            return <section key={store} className="rounded-2xl border border-border bg-background/40 p-4" data-id="element-2088" data-mp="32zmn8hs6txj6">
                    <div className="mb-3 flex items-center justify-between gap-3" data-id="element-2089" data-mp="371gho8bsi5aw">
                      <div className="flex min-w-0 items-center gap-2" data-id="element-2090" data-mp="2t1fj4zizz9yl">
                        <Store className="h-4 w-4 shrink-0 text-primary" data-id="element-2091" data-mp="2t39rled319ls" />
                        <h3 className="truncate font-medium text-foreground" data-id="element-2092" data-mp="2xyp4o439n1h5">{store}</h3>
                      </div>
                      <span className="shrink-0 text-xs text-muted-foreground" data-id="element-2093" data-mp="1hkp7v1zpvry4">
                        {storeItems.length} {storeItems.length === 1 ? 'item' : 'items'}
                      </span>
                    </div>
                    <p className="mb-3 text-sm text-muted-foreground" data-id="element-2094" data-mp="1a2hmedtbsa87">
                      Geschat: <span className="font-medium text-foreground" data-id="element-2095" data-mp="1a4loqrbkt16d">€{estimatedTotal.toFixed(2)}</span>
                    </p>
                    <div className="flex items-end justify-between gap-3" data-id="element-2096" data-mp="1j3ffw2650zcj">
                      <label className="block flex-1 text-sm font-medium text-foreground" data-id="element-2097" data-mp="1j12ypr87et0j">
                        Bedrag op kassabon
                        <div className="relative mt-1.5" data-id="element-2098" data-mp="rcjepytfwj53">
                          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-2099" data-mp="r9wx16cu4n95">€</span>
                          <input type="text" inputMode="decimal" value={actualTotals[store] ?? ''} onChange={event => setActualTotals(current => ({
                      ...current,
                      [store]: event.target.value
                    }))} className="w-full rounded-xl border border-border bg-card py-3 pl-8 pr-4 text-foreground outline-none transition-colors focus:border-primary focus:ring-2 focus:ring-primary/20" aria-label={`Bedrag op kassabon bij ${store}`} data-id="element-2100" data-mp="1558z5ytblg03" />
                        </div>
                      </label>
                      <button type="button" onClick={() => setActualTotals(current => ({
                  ...current,
                  [store]: estimatedTotal.toFixed(2)
                }))} className="rounded-xl border border-border bg-card px-3 py-3 text-xs font-semibold text-primary transition-colors hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-2101" data-mp="1tpz5jsyrjdjy">
                        Gebruik schatting
                      </button>
                    </div>
                  </section>;
          })}
            </div>

            <footer className="border-t border-border bg-background/40 px-6 py-4 pb-safe" data-id="element-2102" data-mp="tp42hzcl2yxp">
              <div className="mb-4 flex items-baseline justify-between gap-4" data-id="element-2103" data-mp="oqsrhlajyidw">
                <div data-id="element-2104" data-mp="jsr5oslqs4yq">
                  <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground" data-id="element-2105" data-mp="ju2cz2eebuul">Totaal betaald</p>
                  <p className="mt-0.5 text-2xl font-bold text-foreground" data-id="element-2106" data-mp="evioovcby6z2">€{actualGrandTotal.toFixed(2)}</p>
                </div>
                <div className="text-right text-sm" data-id="element-2107" data-mp="5szn0eka60ee">
                  <p className="text-muted-foreground" data-id="element-2108" data-mp="5uarkgljvc6k">
                    Geschat €{estimatedGrandTotal.toFixed(2)}
                  </p>
                  {hasMeaningfulDifference && <p className={totalDifference < 0 ? 'mt-0.5 font-medium text-success' : 'mt-0.5 font-medium text-warning'} data-id="element-2109" data-mp="20828802aqwbi">
                      {totalDifference < 0 ? `€${Math.abs(totalDifference).toFixed(2)} minder dan verwacht` : `€${totalDifference.toFixed(2)} meer dan verwacht`}
                    </p>}
                </div>
              </div>
              <div className="flex gap-3" data-id="element-2110" data-mp="148txqb2dz8ab">
                <button type="button" onClick={onClose} className="flex-1 rounded-xl bg-muted py-3 font-medium text-foreground transition-colors hover:bg-muted/80 focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-2111" data-mp="qadtcqrzxtvc">
                  Nog niet klaar
                </button>
                <button type="button" onClick={handleConfirm} disabled={hasInvalidTotal} className="flex-1 rounded-xl bg-primary py-3 font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:bg-muted disabled:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary" data-id="element-2112" data-mp="lc2l2khcnrf8">
                  <span className="flex items-center justify-center gap-2" data-id="element-2113" data-mp="3v6yi15w7vxym">
                    <Check className="h-5 w-5" data-id="element-2114" data-mp="dtman61k9dxr" />
                    Bevestigen
                  </span>
                </button>
              </div>
            </footer>
          </motion.section>
        </motion.div>}
    </AnimatePresence>;
}
function groupItemsByStore(items: ShoppingListItem[]): Record<string, ShoppingListItem[]> {
  return items.reduce<Record<string, ShoppingListItem[]>>((groups, item) => {
    const store = item.store || 'Overig';
    groups[store] = [...(groups[store] ?? []), item];
    return groups;
  }, {});
}